import { useState } from 'react';
import { supabase, TABLES } from '../utils/supabase';
import type { TokenData } from '../types';

export interface SearchResult extends TokenData {
  matchType: 'name' | 'symbol' | 'contract' | 'nft_id';
}

export const usePositionSearch = () => {
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [error, setError] = useState<string | null>(null);

  const search = async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setResults([]);
      return;
    }

    try {
      setSearching(true);
      setError(null);

      const term = searchTerm.trim().toLowerCase();

      console.log('🔍 [usePositionSearch] Searching for:', term);

      // Check if it's a numeric NFT ID
      const isNumericNFT = /^\d+$/.test(term);

      let query = supabase
        .from(TABLES.TOKENS)
        .select('*')
        .eq('network', 'ink');

      if (isNumericNFT) {
        // Search by NFT Token ID (exact match)
        query = query.eq('nft_token_id', term);
      } else if (term.startsWith('0x') && term.length === 42) {
        // Looks like a contract address
        query = query.ilike('contract_address', `%${term}%`);
      } else {
        // Search by name or symbol (partial match)
        query = query.or(`name.ilike.%${term}%,symbol.ilike.%${term}%,contract_address.ilike.%${term}%`);
      }

      const { data, error: searchError } = await query.limit(10);

      if (searchError) {
        throw new Error(searchError.message);
      }

      // Determine match type for each result
      const resultsWithMatchType: SearchResult[] = (data || []).map(token => {
        let matchType: 'name' | 'symbol' | 'contract' | 'nft_id' = 'name';
        
        if (isNumericNFT && token.nft_token_id === term) {
          matchType = 'nft_id';
        } else if (token.contract_address?.toLowerCase().includes(term)) {
          matchType = 'contract';
        } else if (token.symbol?.toLowerCase().includes(term)) {
          matchType = 'symbol';
        } else {
          matchType = 'name';
        }

        return {
          ...token,
          matchType
        };
      });

      console.log('✅ [usePositionSearch] Found results:', {
        count: resultsWithMatchType.length,
        results: resultsWithMatchType.map(r => ({
          name: r.name,
          matchType: r.matchType,
          hasNFT: !!r.nft_token_id
        }))
      });

      setResults(resultsWithMatchType);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Search failed';
      console.error('❌ [usePositionSearch] Error:', errorMessage);
      setError(errorMessage);
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setResults([]);
    setError(null);
  };

  return {
    search,
    results,
    searching,
    error,
    clearSearch
  };
};

